import {
  IGetRelatedNicheParams,
  IGifList,
  INiche,
  INicheFeedbackRequest,
  INicheFeedbackResponse,
  INicheList,
  IPaginatedNicheList,
  IAddToNicheParams,
  IShortUserList,
} from "./types";

import AuthHttpService from "./AuthHttpService";

export default class NichesService extends AuthHttpService {
  public constructor(readonly url?: string) {
    super(url);
  }

  async editNiche(params: {
    id: string;
    description: string;
    image: string | null;
    banner: string | null;
    preferences: string[] | null;
    tags: string[] | null;
  }): Promise<void> {
    return await this.patch(`/v2/niches/${params.id}`, {
      description: params.description,
      image: params.image,
      banner: params.banner,
      preferences: params.preferences,
      tags: params.tags,
    });
  }

  async createNiche(params: {
    name: string;
    description: string;
    rules: string;
    profile_image: string; // upload ticket id
    banner: string | null; // upload ticket id
    tags: string[] | null;
    preferences: string[] | null;
  }): Promise<void> {
    return await this.post("v2/niches", {
      name: params.name,
      description: params.description,
      rules: params.rules,
      profile_image: params.profile_image,
      banner: params.banner,
      tags: params.tags,
      preferences: params.preferences,
    });
  }

  async getNiche(id: string): Promise<INiche> {
    return (await this.get<Promise<{niche: INiche}>>(`v2/niches/${id}`)).niche;
  }

  async getNiches(props: {
    order: string;
    sort?: string;
    previews?: boolean;
    tag?: string;
    page?: number;
    count?: number;
    selected?: string[];
  }): Promise<IPaginatedNicheList> {
    const query = new URLSearchParams();

    query.append("order", props.order);

    if (props.previews) {
      query.append("previews", "yes");
    }

    if (props.sort) {
      query.append("sort", props.sort);
    }

    if (props.tag) {
      query.append("tag", props.tag);
    }

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    if (props.selected) {
      query.append("selected", props.selected.join(","));
    }

    return await this.get(`v2/niches?${query.toString()}`);
  }

  async setGifNiches(props: {
    gifId: string;
    nicheIds: string[];
  }): Promise<void> {
    await this.put(`v2/gifs/${props.gifId}/niches`, {
      niches: props.nicheIds,
    });
  }

  async getFollowingNiches(props: {
    previews: boolean;
    tags?: string[];
    preference?: string;
  }): Promise<INicheList> {
    const query = new URLSearchParams();

    if (props.previews) {
      query.append("previews", "yes");
    }

    if (props.tags && props.tags.length > 0) {
      query.append("tags", props.tags.join(","));
    }

    if (props.preference) {
      query.append("preference", props.preference);
    }

    return await this.get(`v2/niches/following?${query.toString()}`);
  }

  async getMyNiches(props: {
    previews: boolean;
  }): Promise<INicheList> {
    const query = new URLSearchParams();

    if (props.previews) {
      query.append("previews", "yes");
    }

    return await this.get(`v2/niches/my?${query.toString()}`);
  }

  async getTrendingNiches(previews?: boolean): Promise<INicheList> {
    const query = new URLSearchParams();

    if (previews) {
      query.append("previews", "yes");
    }

    return await this.get(`v2/niches/trending?${query.toString()}`);
  }

  async getSuggestedNiches(): Promise<INiche[]> {
    return (await this.get<Promise<INicheList>>("v2/niches/suggest")).niches;
  }

  async addToNiche(params: IAddToNicheParams) {
    return await this.post(`v2/niches/${params.niche_id}/gifs`, { id: params.gif_id });
  }

  async follow(id: string): Promise<void> {
    await this.post(`v2/niches/${id}/subscription`);
  }

  async unfollow(id: string): Promise<void> {
    await this.delete(`v2/niches/${id}/subscription`);
  }

  async getNicheGifs(params: {
    id: string;
    page: number;
    count: number;
    type?: string|undefined;
    order: string;
  }): Promise<IGifList> {
    let url = `v2/niches/${params.id}/gifs?count=${params.count}&page=${params.page}&order=${params.order}`;

    if (params.type !== undefined) {
      url += `&type=${params.type}`;
    }

    return await this.get(url, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async reportBrokenThumbnail(params: {
    niche: string;
    gif: string;
    thumbnail: string;
  }): Promise<void> {
    await this.post(`v2/niches/${params.niche}/missing-gif`, {
      gif: params.gif,
      thumbnail: params.thumbnail,
    });
  }

  /**
   * Send a feedback for a gif.
   * Valid state values: up, down.
   */
  async addFeedback(params: INicheFeedbackRequest): Promise<INicheFeedbackResponse> {
    return await this.post(`v2/niches/${params.niche}/feedback`, {
      gif: params.gif,
      state: params.state,
    });
  }

  async getRelatedNiches({ niche }: IGetRelatedNicheParams) {
    return await this.get<Promise<INicheList>>(`v2/niches/${niche}/related`);
  }

  async getNichesTags({ niche }: IGetRelatedNicheParams) {
    return await this.get<Promise<{ tags: string[] }>>(`v2/niches/${niche}/top-tags`);
  }

  async getNichesCreators({ niche }: IGetRelatedNicheParams) {
    return await this.get<Promise<IShortUserList>>(`v2/niches/${niche}/top-creators`);
  }

  async getGifSuggestedNiche(id: string) {
    return await this.get<Promise<INicheList>>(`v2/gifs/${id}/niches/suggest`);
  }

  async getAddGifSuggestedNiches(tags: string[], preference: string) {
    const query = new URLSearchParams({
      tags: tags.join(","),
      preference
    });

    return await this.get<Promise<INicheList>>(`v2/niches/suggest/adding?${query.toString()}`);
  }
}
